import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { api, apiGet, apiPut, apiPost } from '../../../lib/api.js';
import { normalizePage, savePayload } from '../../../lib/producerPage.js';

export const ME_KEY = ['producer-page', 'me'];
const errorText = (e, fallback) => e?.response?.data?.error || e?.message || fallback;

/**
 * Editor state + actions for the Producer Page wizard.
 * `page` is the local editable copy; `server` is the last saved state (assets, url, problems).
 */
export function useProducerPage() {
  const qc = useQueryClient();
  const catalogQ = useQuery({ queryKey: ['producer-page', 'catalog'], queryFn: () => apiGet('/producer-page/catalog'), staleTime: Infinity });
  const meQ = useQuery({ queryKey: ME_KEY, queryFn: () => apiGet('/producer-page/me'), refetchOnWindowFocus: false });

  const [page, setPage] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rev, setRev] = useState(0); // bumps on every image change so previews refetch
  const [slug, setSlug] = useState({ checking: false });
  const server = meQ.data;

  // Seed the local copy once the first load arrives (later saves keep local edits).
  useEffect(() => {
    if (server?.ok && catalogQ.data && !page) setPage(normalizePage(server.page, catalogQ.data));
  }, [server, page, catalogQ.data]);

  // Debounced page-link availability check.
  const savedSlug = server?.page?.slug || '';
  const seq = useRef(0);
  useEffect(() => {
    const value = page?.slug || '';
    if (!value) { setSlug({ checking: false }); return undefined; }
    if (value === savedSlug) { setSlug({ checking: false, available: true }); return undefined; }
    setSlug({ checking: true });
    const mine = ++seq.current;
    const t = setTimeout(async () => {
      try {
        const r = await apiGet('/producer-page/slug-check', { slug: value });
        if (mine === seq.current) setSlug({ checking: false, available: r.available, error: r.error, slug: r.slug });
      } catch { if (mine === seq.current) setSlug({ checking: false, available: false, error: 'Could not check that link.' }); }
    }, 350);
    return () => clearTimeout(t);
  }, [page?.slug, savedSlug]);

  const patch = (changes) => setPage((p) => ({ ...p, ...changes }));
  const setServer = (data) => qc.setQueryData(ME_KEY, data);

  /** Save the draft; resolves true on success. */
  async function save({ quiet = false, override = {} } = {}) {
    setBusy(true);
    try {
      const r = await apiPut('/producer-page/me', savePayload({ ...page, ...override }));
      setServer(r);
      if (!quiet) toast.success('Saved');
      return true;
    } catch (e) {
      toast.error(errorText(e, 'Could not save'));
      return false;
    } finally { setBusy(false); }
  }

  /** Upload one image ('profile' | 'background' | 'license'). */
  async function upload(kind, file, state = '') {
    const body = new FormData();
    body.append('file', file);
    try {
      const r = await api.post(`/producer-page/assets/${kind}${state ? `/${state}` : ''}`, body).then((x) => x.data);
      setServer(r);
      setRev((n) => n + 1);
      return true;
    } catch (e) { toast.error(errorText(e, 'Upload failed')); return false; }
  }

  async function remove(kind, state = '') {
    try {
      const r = await api.delete(`/producer-page/assets/${kind}${state ? `/${state}` : ''}`).then((x) => x.data);
      setServer(r);
      setRev((n) => n + 1);
      return true;
    } catch (e) { toast.error(errorText(e, 'Could not delete')); return false; }
  }

  async function publish(on) {
    if (on && !(await save({ quiet: true }))) return false; // publish what the agent sees, not a stale draft
    setBusy(true);
    try {
      const r = await apiPost(on ? '/producer-page/publish' : '/producer-page/unpublish', {});
      setServer(r);
      qc.invalidateQueries({ queryKey: ['onboarding', 'checklist'] });
      qc.invalidateQueries({ queryKey: ['producer-page', 'status'] });
      toast.success(on ? 'Producer page is live' : 'Producer page unpublished');
      return true;
    } catch (e) {
      toast.error(errorText(e, 'Could not publish'));
      return false;
    } finally { setBusy(false); }
  }

  const assets = useMemo(() => server?.assets || { profile: false, background: false, licenses: [] }, [server]);
  return {
    loading: meQ.isLoading || catalogQ.isLoading || !page,
    error: meQ.isError || catalogQ.isError,
    catalog: catalogQ.data, page, patch, server, assets, slug, busy, rev, save, upload, remove, publish,
  };
}
